import React, { useContext, Fragment, useMemo, useState, useCallback, useEffect } from 'react'
import { ThemeContext } from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import Card from 'components/Card'
import Button from 'components/Button'
import Breadcrumb from 'components/Breadcrumb'
import { RadioGroup, TextField, Dropzone, CheckboxField, SelectField } from 'components/Form'
import { SemiTitle, Title, Text } from 'components/Typography'

import { attributeBeneficiary, beneficiariesAttribution } from 'services/groups'
import { searchBeneficiaryByCpf } from 'services/beneficiaries'
import Loader from 'components/Loader'
import { beneficiaryDocumentMask } from 'helpers/masks'

const validationSchema = Yup.object().shape({
  name: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string().required(),
    otherwise: Yup.string().notRequired(),
  }),
  document: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string()
      .min(14, 'CPF ou CNPJ inválido')
      .required(),
    otherwise: Yup.string().notRequired(),
  }),
  email: Yup.string().when('form_type', {
    is: value => value === 'single',
    then: Yup.string()
      .email()
      .required(),
    otherwise: Yup.string().notRequired(),
  }),
})

const AddBeneficiary = () => {
  const theme = useContext(ThemeContext)
  const { id: groupID, name: groupName } = useParams()
  const [csvError, setCsvError] = useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [documentValue, setDocumentValue] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [initialValues, setInitialValues] = useState({
    form_type: 'single',
    csv: '',
    name: '',
    document: '',
    email: '',
  })
  const [isBeneficiaryFound, setBeneficiaryFound] = useState(false)
  const [isInputsEnabled, setInputsEnabled] = useState(false)

  const dateOptions = useMemo(() => Array.from(Array(30).keys()).map(day => ({ label: day + 1, value: day + 1 })), [])

  const breadcrumbPath = useMemo(
    () => [
      {
        label: 'Grupos de beneficiários',
        link: '/prepaid-purchase/groups',
      },
      {
        label: groupName,
        link: `/prepaid-purchase/groups/attribution/${groupID}`,
      },
      {
        label: 'Adicionar beneficiário',
      },
    ],
    [groupID, groupName],
  )

  const handleSubmit = useCallback(
    async ({ form_type, csv, name, email, document, activate_user, activation_day }, { resetForm }) => {
      try {
        setIsSubmitting(true)

        if (form_type === 'group') {
          const formattedFile = new FormData()
          formattedFile.append('file', csv)

          const {
            data: { errors },
          } = await beneficiariesAttribution(groupID, formattedFile)

          if (errors.length) {
            toast.warn('Alguns dados do CSV não puderam ser inseridos.')
            const exception = new Error()

            exception.response = {
              data: errors,
            }

            throw exception
          }
        }

        if (form_type === 'single') {
          const formattedValues = activate_user
            ? {
                name,
                document,
                email,
                status: 'active',
              }
            : {
                name,
                document,
                email,
                status: activation_day?.value ? 'waiting' : 'inactive',
                ...(activation_day && { activation_day: activation_day.value }),
              }

          await attributeBeneficiary(groupID, formattedValues)
        }

        toast.success('Cadastro realizado com sucesso!')
        resetForm()
      } catch (e) {
        console.log(e)
        if (form_type === 'group') {
          const formattedError = e?.response.data.map(error => (
            <Flex>
              <SemiTitle color={theme.colors.error}>Linha {error.line}:&nbsp;</SemiTitle>
              <Text color={theme.colors.error}>{error.errors.map(log => log).join(' ')}</Text>
            </Flex>
          ))
          setCsvError(formattedError)
        }

        if (form_type === 'single') {
          e?.response?.data?.document
            ? toast.error('CPF ou CNPJ inválido')
            : toast.error('Não foi possível realizar o cadastro.')
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [],
  )

  useEffect(() => {
    const searchBeneficiary = async () => {
      if (documentValue?.length === 14 || documentValue?.length === 18) {
        setIsSearching(true)
        const { data } = await searchBeneficiaryByCpf(documentValue.replace(/\D+/g, ''))

        if (data.length > 0) {
          const { name, email, status, activation_day } = data[0]
          setInitialValues({
            form_type: 'single',
            csv: '',
            name,
            document: documentValue,
            email,
            status: status === 'active' ? false : true,
            activate_user: status === 'active',
          })
          setBeneficiaryFound(true)
        } else {
          setBeneficiaryFound(false)
          setInitialValues({ ...initialValues, name: '', email: '' })
        }
        setIsSearching(false)
      }
    }

    searchBeneficiary()
  }, [documentValue])

  useEffect(() => {
    setInputsEnabled((documentValue?.length === 14 || documentValue?.length === 18) && !isBeneficiaryFound)
  }, [isBeneficiaryFound, documentValue])

  return (
    <Flex flexDirection='column' width={1}>
      <Breadcrumb children={breadcrumbPath} />
      <Title as='h3'>Adicionar beneficiário</Title>
      <Card width={1} p={24} mt={24} bg={theme.colors.white}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <RadioGroup
                label=''
                mb={32}
                direction='row'
                name='form_type'
                options={[
                  { label: 'Apenas um beneficiário', value: 'single' },
                  { label: 'Diversos beneficiários', value: 'group' },
                ]}
              />
              {values.form_type === 'single' ? (
                <Flex flexDirection='column' width='280px'>
                  <TextField
                    name='document'
                    type='text'
                    label='CPF/CNPJ'
                    placeholder='CPF ou CNPJ'
                    onChange={e => {
                      setFieldValue('document', e.target.value)
                      setDocumentValue(e.target.value)
                    }}
                    mask={beneficiaryDocumentMask}
                    guide={false}
                    mb={32}
                  />
                  {isSearching ? (
                    <Flex minHeight={144} justifyContent='center'>
                      <Loader />
                    </Flex>
                  ) : (
                    <Fragment>
                      <TextField
                        name='name'
                        type='text'
                        label='Nome do cliente'
                        placeholder='Nome do cliente'
                        mb={32}
                        disabled={!isInputsEnabled}
                      />
                      <TextField
                        name='email'
                        type='email'
                        label='E-mail'
                        placeholder='E-mail'
                        mb={32}
                        disabled={!isInputsEnabled}
                      />
                      <SelectField
                        name='activation_day'
                        placeholder='Dia de cobrança'
                        label='Dia de cobrança'
                        options={dateOptions}
                        mb={32}
                        disabled={values.activate_user || !isInputsEnabled}
                      />
                      <CheckboxField name='activate_user' label='Usuário ativo' mb={32} disabled={!isInputsEnabled} />
                    </Fragment>
                  )}
                </Flex>
              ) : (
                <Dropzone
                  setFieldValue={setFieldValue}
                  name='csv'
                  label='Upload da lista de clientes'
                  mb={32}
                  errors={csvError}
                />
              )}
              <Flex width={584} justifyContent='space-between'>
                <Button
                  as={Link}
                  to={`/prepaid-purchase/groups/attribution/${groupID}`}
                  size='big'
                  type='button'
                  outlined
                >
                  Voltar
                </Button>
                <Button size='big' type='submit' disabled={isSubmitting}>
                  {isSubmitting ? <Loader /> : 'Adicionar'}
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Card>
    </Flex>
  )
}

export default AddBeneficiary
